import _ from 'lodash';
import React, { Component } from 'react';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { isMobile } from 'react-device-detect';
import toastr from '../../../toastr';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import PartLookup from '../lookups/PartLookup';
import DefaultPartModal from '../modals/DefaultPartModal';
import DefaultPartSelectorModal from '../modals/DefaultPartSelectorModal';
import NonStockingPartModal from '../modals/NonStockingPartModal';
import { scrollInvoiceBuilder } from './InvoiceBuilder';

interface IProps {
  model: Model;
  onBillOfMaterialsLoad: (billOfMaterialsID: string) => void;
  onFleetcrossInquiry: Function;
}

const getContainer = () => {
  return document.getElementById('shopkeeper');
};

@observer
class LineItemEntry extends Component<IProps, {}> {
  model: Model;

  partEntryInput: HTMLInputElement;
  multiplePartsTextArea: HTMLTextAreaElement;
  nonStockingPart: HTMLInputElement;
  partLookupRef: PartLookup;
  partCommentInput: HTMLInputElement;

  @observable showAddPartListModal = false; // multi part entry
  @observable showDefaultPartModal = false; // ATB3 default part entry
  @observable showDefaultPartSelectorModal = false; // part entry helper for default parts
  @observable showNonStockingPartModal = false; // @nonstocking parts

  // PIN4 & PSO3 disables non-tracked and default parts
  isPIN4: boolean;
  isPSO3: boolean;

  // SMM3 disables most line item features if the line isn't a sublet or default part
  isSMM3: boolean;

  multiPartEntryDefaultDivision: string;
  partNumEntry: string;
  partNumQtyEntry: number;
  unpriced: boolean;

  constructor(props: IProps) {
    super(props);
    this.model = props.model;
    this.isPIN4 = props.model.runMode === 'PIN4';
    this.isPSO3 = props.model.runMode === 'PSO3';
    this.isSMM3 = props.model.runMode === 'SMM3';
  }

  actionPreCheck = () => {
    // PIN1 has no action pre-checks

    // PRO1/SMM3/SRE2: We allow fast part lookup, but if a RO is loaded there must be an active operation chosen
    if (['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode)) {
      if (this.model.activeRONumber !== null) {
        if (this.model.activeOperation === 0) {
          toastr.error('Error', 'Please select an operation to issue to first.');
          return false;
        }
        const locked = this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].locked;
        const voided = this.model.activeRepairOrderDetails.operationDetails[this.model.activeOperation].voided;

        if (this.model.activeRONumber !== null && this.model.activeOperation !== 0 && (locked || voided)) {
          toastr.error('Error', `Operation ${locked ? 'Locked!' : 'Voided!'}`);
          return false;
        }
      } // else just doing a PRO1 fast inquiry (for checking quantity, etc.)
    }

    return true;
  };

  // Slight misnomer - this accepts multiple comments as well
  addMultipleParts = async () => {
    const items: Array<{ partNum: string; qty: number }> = [];
    const inputLines = this.multiplePartsTextArea.value.split('\n');
    let invalidPartDetected = false;

    inputLines.forEach((inputLine, index) => {
      inputLine = inputLine.trim();
      if (inputLine !== '') {
        // ignore forces or non-stocking parts
        if (_.includes(['*', '$', '@'], inputLine[0])) {
          toastr.error('Error', `*,@,$ parts not allowed. (Line ${index + 1})`);
          invalidPartDetected = true;
          return;
        }

        // comment line?
        if (inputLine[0] === '#') {
          items.push({ partNum: inputLine, qty: 1 });
          return;
        }

        // ignore anything after first space on non-comment line
        inputLine = inputLine.split(' ')[0];

        if (this.model.defaultParts.has(inputLine.toUpperCase())) {
          toastr.error('Error', `ATB3 default parts are not allowed. (Line ${index + 1})`);
          invalidPartDetected = true;
          return;
        }

        let params;
        // QTY.DIV:PARTNUM
        // eslint-disable-next-line no-cond-assign
        if ((params = inputLine.match(/^(\d{1,4})\.([A-Za-z]):(.*)$/))) {
          items.push({ partNum: `${params[2]}:${params[3]}`, qty: parseInt(params[1], 10) });

          // DIV:PARTNUM
          // eslint-disable-next-line no-cond-assign
        } else if ((params = inputLine.match(/^([A-Za-z]):(.*)$/))) {
          items.push({ partNum: `${params[1]}:${params[2]}`, qty: 1 });

          // DEFAULT DIVISION + QTY.PARTNUM
          // eslint-disable-next-line no-cond-assign
        } else if (!!this.multiPartEntryDefaultDivision && (params = inputLine.match(/^(\d{1,4})\.(.*)$/))) {
          items.push({
            partNum: `${this.multiPartEntryDefaultDivision.toUpperCase()}:${params[2]}`,
            qty: parseInt(params[1], 10),
          });

          // DEFAULT DIVISION + PARTNUM
        } else if (!!this.multiPartEntryDefaultDivision) {
          items.push({
            partNum: `${this.multiPartEntryDefaultDivision.toUpperCase()}:${inputLine}`,
            qty: 1,
          });

          // Assume Error
        } else {
          toastr.error('Error', `Invalid Format (Line ${index + 1})`);
          invalidPartDetected = true;
        }
      }
    });

    // Process if we have no errors
    if (!invalidPartDetected) {
      // Close the modal
      this.showAddPartListModal = false;

      // Anything to submit?
      if (items.length > 0) {
        await this.model.addMultipleLineItems(items);
        // do a full scroll after all items are backfilled
        scrollInvoiceBuilder();
      }
    }
  };

  clear() {
    this.partEntryInput.value = '';

    if (!this.isSMM3) {
      this.partCommentInput.value = '';
      this.partLookupRef.clear();
    }

    if (!['PIN4', 'PSO3', 'SMM3'].includes(this.model.runMode)) {
      this.nonStockingPart.checked = false;
    }
  }

  commentEntered() {
    if (!this.actionPreCheck()) {
      return;
    }

    if (this.model.modelLocked) {
      toastr.error('Wait', 'Action in process. Please try again in a moment.');
      setTimeout(() => this.partCommentInput.focus(), 50);
      return;
    }

    this.model.addLineItemComment(this.partCommentInput.value);
    this.partCommentInput.value = '';
    this.partLookupRef.clear();
    this.focusPartEntry();
  }

  focusPartEntry(moveCursorToStart: boolean = false) {
    setTimeout(() => {
      this.partEntryInput.focus();
      if (moveCursorToStart) {
        this.partEntryInput.setSelectionRange(0, 0);
      }
    }, 50);
  }

  loadPartEntryFromLookup = (partNumber: string) => {
    this.partEntryInput.value = partNumber;
    this.focusPartEntry(true);
  };

  // parse / add to model here
  async partEntered() {
    if (!this.actionPreCheck()) {
      return;
    }

    if (this.model.modelLocked) {
      toastr.error('Wait', 'Action in process. Please try again in a moment.');
      this.focusPartEntry();
      return;
    }

    let val = this.partEntryInput.value.trim();
    let params;

    let division = this.model.activeDivision.code; // default to current division
    let forced = false;
    let partNum = '';
    let quantity = 1;

    /* Syntax Possibilities:
       -:BILLOFMATERIALSID (backed by PIN4 entry)
       DEFAULTPART (backed by ATB3 default part entry)
       #lineitemcomment
       @NONSTOCKING_PARTNUM, *FORCED_PARTNUM
       2.DIV:PARTNUM, 2.PARTNUM, DIV:PARTNUM, PARTNUM
       (can apply @ or * to syntaxes of line 2)
       (QTY: 1-9999, DIV: single char A-Z/a-z)
    */

    if (val.startsWith('$') && (['SRE2'].includes(this.model.runMode) || this.model.isEstimate)) {
      toastr.error('Error', 'Unpriced parts not available on estimates.');
      return;
    }

    if (val.startsWith('-:') && !this.isSMM3) {
      this.partEntryInput.value = '';
      this.props.onBillOfMaterialsLoad(val.slice(2).toUpperCase());
      return;
    }

    // Default part entered such as FREON, DISCOUNT, HNDLCHG
    if (this.model.defaultParts.has(val.toUpperCase())) {
      if (this.isPSO3) {
        toastr.error('Error', `Default Parts Not Allowed in ${this.model.runMode}`);
        return;
      }

      runInAction(() => {
        this.partNumEntry = val.toUpperCase();
        this.unpriced = false;
        this.showDefaultPartModal = true;
      });
      return;
    }

    // Default part entered as @FREON or *FREON (legacy supports)
    if (_.includes(['@', '*'], val[0]) && this.model.defaultParts.has(val.slice(1).toUpperCase())) {
      if (this.isPSO3) {
        toastr.error('Error', `Default Parts Not Allowed in ${this.model.runMode}`);
        return;
      }

      runInAction(() => {
        this.partNumEntry = val.slice(1).toUpperCase();
        this.unpriced = false;
        this.showDefaultPartModal = true;
      });
      return;
    }

    // Unpriced default part scenario support below (PRO1/SMM3 only)

    // Line item comment indicated by leading #
    if (val[0] === '#') {
      const comment = val.trim().substr(1);

      // prevent empty comments
      if (!comment) {
        return;
      }

      if (this.isSMM3) {
        toastr.error('Error', 'Sublets & Default Parts Only');
        return;
      }

      this.model.addLineItemComment(comment);

      // prepare for next part entry
      this.partEntryInput.value = '';
      return;
    }

    // Detect non-stocking / unpriced / forced part indicator (continue parse ex-post)
    const nonStockSyntax = val[0] === '@';
    const unpricedSyntax = val[0] === '$';

    // SMM3 pops a more general error below referencing only sublets and default parts if non-tracked is entered
    if (nonStockSyntax && this.model.sysParams.disableNonTrackedParts && !this.isSMM3) {
      toastr.error('Error', 'Non-tracked part entry is disabled.');
      return;
    }

    if (nonStockSyntax && (this.isPIN4 || this.isPSO3)) {
      toastr.error('Error', `Non-Tracked Parts Not Allowed in ${this.model.runMode}`);
      return;
    }

    if (unpricedSyntax && ['PIN1', 'PIN4', 'PSO3'].includes(this.model.runMode)) {
      toastr.error('Error', `Unpriced Parts Not Allowed in ${this.model.runMode}`);
      return;
    }

    if (
      nonStockSyntax ||
      unpricedSyntax ||
      (!this.isPIN4 && !this.isPSO3 && !this.isSMM3 && this.nonStockingPart.checked)
    ) {
      // only strip leading character if "old school" syntax is used
      if (nonStockSyntax || unpricedSyntax) {
        partNum = val.substr(1);
      } else {
        partNum = val;
      }

      // Unpriced default part support: $SUBL, for example, is a sublet that keeps the RO open until priced
      if (unpricedSyntax && this.model.defaultParts.has(partNum.toUpperCase())) {
        runInAction(() => {
          this.partNumEntry = partNum.toUpperCase();
          this.unpriced = true;
          this.showDefaultPartModal = true;
        });
        return;
      }

      if (!this.isSMM3) {
        // N.PART syntax utilized?, strip off quantity
        // eslint-disable-next-line no-cond-assign
        if ((params = partNum.match(/^(\d{1,4})\.(.*)$/))) {
          quantity = parseInt(params[1], 10);
          partNum = params[2];
        }

        runInAction(() => {
          this.partNumEntry = partNum;
          this.partNumQtyEntry = quantity;
          this.unpriced = unpricedSyntax;
          this.showNonStockingPartModal = true;
        });

        return;
      }

      // If this is a stocking part entered with legacy *FORCE syntax, pop a pricing override modal.
    } else if (val[0] === '*') {
      if (this.isPIN4) {
        toastr.error('Error', `Forces not allowed in ${this.model.runMode}`);
        return;
      }

      forced = true;
      val = val.substr(1);
    }

    // Detect errant entry directly after @ or *, allow continuation
    if (!val) {
      return;
    }

    // If we got here in SMM3, this is not an accepted entry
    if (this.isSMM3) {
      toastr.error('Error', 'Sublets & Default Parts Only');
      return;
    }

    // FORM: 2.DIV:PARTNUM
    // eslint-disable-next-line no-cond-assign
    if ((params = val.match(/^(\d{1,4})\.([A-Za-z]):(.*)$/))) {
      quantity = parseInt(params[1], 10);
      division = params[2];
      partNum = params[3];

      // FORM: 2.PARTNUM
      // eslint-disable-next-line no-cond-assign
    } else if ((params = val.match(/^(\d{1,4})\.(.*)$/))) {
      quantity = parseInt(params[1], 10);
      partNum = params[2];
      // assume current division

      // FORM: DIV:PARTNUM
      // eslint-disable-next-line no-cond-assign
    } else if ((params = val.match(/^([A-Za-z]):(.*)$/))) {
      division = params[1];
      partNum = params[2];
      // assume default quantity of 1

      // FORM: PARTNUM
    } else {
      partNum = val;
      // assume current division
      // assume default quantity of 1
    }

    division = division.toUpperCase();

    // if (process.env.NODE_ENV === 'development') {
    //   console.log('');
    //   console.warn(`PART: ${partNum}, DIV: ${division}, QTY: ${quantity}`);
    //   console.warn(`NONSTOCK: ${nonStocking}, FORCED: ${forced}`);
    // }

    // check for valid part division before proceeding
    // on failure, leave existing part input available for editing (typo, etc.)
    if (!this.model.partDivisions.has(division)) {
      toastr.error('Error', `Part division "${division}" does not exist.`);
      this.focusPartEntry();
      return;
    }

    if (partNum[partNum.length - 1] === '#') {
      toastr.error('Error', 'You may not add a clean core directly.');
      this.focusPartEntry();
      return;
    }

    // prepare for next part entry
    const partInputRestore = this.partEntryInput.value; // BAD PART scenarios
    this.partEntryInput.value = '';
    this.partLookupRef.clear();

    // Add part to lineItems in model.  This may have the effect of changing active division.
    if (await this.model.addStockingPartLineItem(partNum, division, quantity, forced)) {
      /* I should be ashamed of this hack, but it works very easily via the DOM.
         auto-scrolls InvoiceBuilder window to bottom of lineitems on successful line item
         addition.  See InvoiceBuilder.tsx for other auto-scroll logic.  Unfortunately, the
         autoscroll logic within InvoiceBuilder doesn't fire once the line item expands on
         detail backfill, and as a result will only show the first row of the final line item
         requiring further scrolling on a multi-line detail backfill. This hack solves that.
      */
      if (this.model.lineItems.length > 0) {
        scrollInvoiceBuilder();
      }

      // Toasters may pop during process (part correction, etc.), which steals focus
      this.focusPartEntry();

      // Otherwise we have a "BAD PART" (most likely), or some sortof other error scenario,
      // so here we'll give the user a chance to review & modify their original input
    } else {
      this.partEntryInput.value = partInputRestore;
      this.focusPartEntry();
    }
  }

  render() {
    let netPriceType = '';

    switch (this.model.sysParams.partsInquiryDisplayOptions) {
      case 'T2':
        netPriceType = 'TRADE2';
        break;
      case 'T3':
        netPriceType = 'TRADE3';
        break;
      // case 'N', 'W', 'X', ''
      default:
        netPriceType = 'NET';
        break;
    }

    return (
      <div className="sk-partEntryContainer">
        <div className="sk-partEntry">
          <Label>
            Part Entry
            {!this.isSMM3 && this.model.activeDivision.code && (
              <span title={this.model.activeDivision.desc}> - (DIV: {this.model.activeDivision.code})</span>
            )}
          </Label>
          <input
            type="text"
            onKeyPress={evt => {
              if (evt.key === 'Enter' && this.partEntryInput.value.trim().length) {
                this.partEntered();
              }
            }}
            onKeyDown={evt => {
              if (evt.key === 'Tab') {
                const copyOverValue = this.partEntryInput.value.trim();
                const existingSearchValue = (document.getElementById('partLookupSK') as HTMLInputElement).value.trim();

                if (copyOverValue.length > 0 && existingSearchValue === '') {
                  this.partLookupRef.value = copyOverValue;
                }
              }
            }}
            ref={(ref: HTMLInputElement) => (this.partEntryInput = ref)}
          />
          {isMobile && (
            <div style={{ display: 'inline-block', marginLeft: '3px' }}>
              <Button
                size="sm"
                variant="success"
                onClick={() => {
                  if (this.partEntryInput.value.trim().length) {
                    this.partEntered();
                  }
                }}
              >
                +
              </Button>
            </div>
          )}
          <div className="sk-partEntryActionsContainer">
            {this.isPIN4 || this.isPSO3 || this.isSMM3 || this.model.sysParams.disableNonTrackedParts ? (
              <div />
            ) : (
              <FormCheck inline={true}>
                <FormCheck.Input
                  type="checkbox"
                  id="nonStocking"
                  className="sk-useMaterialInput sk-nonTrackedOption"
                  tabIndex={-1}
                  ref={(ref: HTMLInputElement) => (this.nonStockingPart = ref)}
                />
                <FormCheck.Label htmlFor="nonStocking">Non-Tracked</FormCheck.Label>
              </FormCheck>
            )}
            <div className="sk-partEntryActions">
              {!this.isPSO3 && (
                <i
                  className="zmdi zmdi-money sk-partEntryActionItem"
                  title="Additional Line Items"
                  onClick={() => {
                    if (!this.actionPreCheck()) {
                      return;
                    }
                    this.showDefaultPartSelectorModal = true;
                  }}
                />
              )}
              &nbsp;&nbsp;
              {!this.isSMM3 && (
                <>
                  <i
                    className="zmdi zmdi-file-text sk-partEntryActionItem"
                    title="Multiple Part Entry"
                    onClick={() => {
                      if (!this.actionPreCheck()) {
                        return;
                      }
                      this.multiPartEntryDefaultDivision = '';
                      this.showAddPartListModal = true;
                    }}
                  />
                  &nbsp;&nbsp;
                </>
              )}
              <i
                className="zmdi zmdi-archive sk-partEntryActionItem"
                title="Copy Parts to Clipboard"
                onClick={() => {
                  this.model.copyLineItemsToClipboard();
                }}
              />
              {this.model.sysParams.fleetcross && !this.isSMM3 && (
                <>
                  &nbsp;&nbsp;
                  <i
                    className="zmdi zmdi-cloud-outline-alt sk-partEntryActionItem"
                    title="Fleetcross Inquiry"
                    onClick={() => this.props.onFleetcrossInquiry()}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        {!this.isSMM3 && (
          <div className="sk-searchTerm">
            <Label>Part Search</Label>
            <PartLookup
              ref={(ref: PartLookup) => (this.partLookupRef = ref)}
              highlightSupercededSearch={this.model.sysParams.highlightSupercededSearch}
              loadPartEntryFromLookup={this.loadPartEntryFromLookup}
            />
          </div>
        )}
        {!this.isSMM3 && (
          <div className="sk-memoComment">
            <Label>Memo / Comment</Label>
            <input
              type="text"
              onKeyPress={evt => {
                if (evt.key === 'Enter' && this.partCommentInput.value.trim().length) {
                  this.commentEntered();
                }
              }}
              maxLength={90}
              ref={(ref: HTMLInputElement) => (this.partCommentInput = ref)}
            />
          </div>
        )}

        {/* The following are various modals which conditionally render into the DOM based on the active line item entry type. */}

        {/* This shows a modal which allows the user to select a default part for entry with a click. */}
        {this.showDefaultPartSelectorModal && (
          <DefaultPartSelectorModal
            apply={(defaultPartNum: string) =>
              runInAction(() => {
                this.partNumEntry = defaultPartNum.toUpperCase();
                this.showDefaultPartSelectorModal = false;
                this.showDefaultPartModal = true;
              })
            }
            container={getContainer}
            hide={() => (this.showDefaultPartSelectorModal = false)}
            model={this.model}
            show={this.showDefaultPartSelectorModal}
          />
        )}

        {/* For intial default part entry.  This usage differs from default part line item edits ex-post. */}
        {this.showDefaultPartModal && (
          <DefaultPartModal
            apply={(
              netPrice: number,
              listPrice: number,
              tradePrice: number,
              description: string,
              subletComment: string,
              unpriced: boolean
            ) => {
              this.model.addLineItemDefaultPart(
                this.partNumEntry,
                netPrice,
                listPrice,
                tradePrice,
                description,
                subletComment,
                unpriced
              );
              this.showDefaultPartModal = false;
              this.partEntryInput.value = '';
            }}
            container={getContainer}
            hide={() => (this.showDefaultPartModal = false)}
            data={{
              defaultPartNum: this.partNumEntry,
              description: '',
              listPrice: '',
              netPrice: '',
              subletComment: '',
              tradePrice: '',
            }}
            model={this.model}
            show={this.showDefaultPartModal}
            showDefaultNetPrice={true}
            unpricedOptionAvailable={['PRO1', 'SMM3'].includes(this.model.runMode) && !this.model.isEstimate}
            unpricedOptionChecked={this.unpriced}
          />
        )}

        {/* For intial non-stocking part entry.  This usage differs from non-stocking line item edits ex-post. */}
        {this.showNonStockingPartModal && (
          <NonStockingPartModal
            apply={(
              partNum: string,
              netPrice: number,
              listPrice: number,
              tradePrice: number,
              description: string,
              stockClass: string,
              priceSymbol: string,
              unpriced: boolean
            ) => {
              this.model.addNonStockingPartLineItem(
                partNum,
                this.partNumQtyEntry,
                netPrice,
                listPrice,
                tradePrice,
                description,
                stockClass,
                priceSymbol,
                unpriced
              );

              this.showNonStockingPartModal = false;
              this.partEntryInput.value = '';
              this.nonStockingPart.checked = false;
            }}
            container={getContainer}
            hide={() => (this.showNonStockingPartModal = false)}
            data={{
              description: '',
              listPrice: '',
              netPrice: '',
              partNum: this.partNumEntry,
              priceSymbol: '',
              stockClass: '',
              tradePrice: '',
            }}
            model={this.model}
            show={this.showNonStockingPartModal}
            unpricedOptionAvailable={['PRO1', 'SMM3'].includes(this.model.runMode) && !this.model.isEstimate}
            unpricedOptionChecked={this.unpriced}
          />
        )}

        {/* This modal allows the user to add multiple parts at once, perhaps from an external text file. */}
        {this.showAddPartListModal && (
          <Modal
            backdrop="static"
            size="lg"
            container={getContainer}
            dialogClassName="sk-multiPartEntryModal"
            onHide={() => (this.showAddPartListModal = false)}
            show={this.showAddPartListModal}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Multiple Part Entry</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <ul>
                <li>Enter one part per line.</li>
                <li>You must specify the division per item DIV:PARTNUM or QTY.DIV:PARTNUM by default.</li>
                <li>If using a default division (below), it will be applied to any item lacking a division.</li>
                <li>#Comments are also supported.</li>
                <li>Forces/non-stocking parts are not supported.</li>
                <li>You can make a note to yourself after a part number if you wish (following a space).</li>
              </ul>
              <textarea
                autoFocus={true}
                className="sk-addPartListTextArea"
                ref={(ref: HTMLTextAreaElement) => (this.multiplePartsTextArea = ref)}
              />
            </Modal.Body>
            <Modal.Footer>
              <div className="sk-modalToolbar">
                <div>
                  Default Division:
                  <input
                    defaultValue={this.multiPartEntryDefaultDivision}
                    onChange={evt => {
                      const value = evt.target.value;
                      if (value === '' || /[A-Za-z]/.test(value)) {
                        this.multiPartEntryDefaultDivision = value;
                      } else {
                        toastr.error('Error', 'Invalid default division specified.');
                        this.multiPartEntryDefaultDivision = '';
                      }
                    }}
                    type="text"
                    maxLength={1}
                    style={{ marginLeft: '10px', width: '40px' }}
                  />
                </div>
                <div>
                  <Button onClick={() => (this.showAddPartListModal = false)} variant="secondary">
                    Cancel
                  </Button>
                  <Button onClick={this.addMultipleParts} variant="success">
                    Add Parts
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </Modal>
        )}

        {/* This modal is only shown if a part comes back as "bad" with > 1 cross reference (1:1 is auto-promoted) */}
        {this.model.showCrossReferencesModal && (
          <Modal
            backdrop="static"
            size="lg"
            container={getContainer}
            onHide={() => (this.model.showCrossReferencesModal = false)}
            show={this.model.showCrossReferencesModal}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>Cross Reference Selection</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <table className="table">
                <thead>
                  <tr>
                    <th>PART</th>
                    <th>PART DESC</th>
                    <th>QTY ON HAND</th>
                    <th>{netPriceType}</th>
                    <th>LIST</th>
                    <th>SELL</th>
                  </tr>
                </thead>
                <tbody>
                  {this.model.activeCrossReferenceOptions.map((crossReference, index) => {
                    let netPriceDisplay = '';
                    switch (this.model.sysParams.partsInquiryDisplayOptions) {
                      case 'N':
                        netPriceDisplay = this.model.sysParams.displayNetOnPartsInquiry ? crossReference.netPrice : '';
                        break;
                      case 'T2':
                        netPriceDisplay = crossReference.trade2Price;
                        break;
                      case 'T3':
                        netPriceDisplay = crossReference.trade3Price;
                        break;
                      default:
                        // 'W', 'X', or nothing
                        break;
                    }

                    return (
                      <tr
                        className="sk-selectorRow"
                        key={index}
                        onClick={() => {
                          this.model.showCrossReferencesModal = false;
                          this.model.addStockingPartLineItem(crossReference.partNum, '', 1, false, true);
                        }}
                      >
                        <td>{crossReference.partNum}</td>
                        <td>{crossReference.partDesc}</td>
                        <td>{crossReference.partQtyOnHand}</td>
                        <td>{netPriceDisplay}</td>
                        <td>{crossReference.listPrice}</td>
                        <td>{crossReference.tradePrice}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => (this.model.showCrossReferencesModal = false)} variant="secondary">
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    );
  }
}

export default LineItemEntry;
